import DashboardLayout from "components/layouts/dashboard-layout";
import PageLinks from "components/page-links";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import StepOne from "./components/steps/step-1";
import styled from "styled-components";

import {
  IMusicData,
  ITrack,
  ITrackSplit,
  MusicDataKeys
} from "types/musicData";
import StepTwo from "./components/steps/step-2";

import StepThree from "./components/steps/step-3";
import Track from "./components/steps/track";
import StepFour from "./components/steps/step-4";
import StepFive from "./components/steps/step-5";
import { stores } from "constants/stores";
import FinishModal from "./components/finish-modal";
import ProgressSteps from "components/progress-steps";
import StepsNav from "components/steps-nav";
import { addDays } from "utils/dateConvert";
import { Countries } from "constants/countries";
import { scrollToTop } from "utils/scrollToTop";

const UploadMusic = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [step2Checklist, setStep2Checklist] = useState(false);
  const [activeTrack, setActiveTrack] = useState<number | undefined>();
  const [finish, setFinish] = useState(false);

  const initialData: IMusicData = {
    releaseTitle: "",
    recordLabel: "Rated Afrika",
    releaseType: "Single",
    primaryArtist: { id: "", name: "" },
    releaseLanguage: "",
    primaryGenre: "",
    secondaryGenre: "",
    coverImage: undefined,
    trackList: [],
    schedule: {
      trackPrice: 0.69,
      originalReleaseDate: new Date(),
      proposedReleaseDate: addDays(new Date(), 7),
      preorderDate: new Date(),
      countries: [...Countries],
      releaseTime: "12:00"
    },
    stores: [...stores]
  };

  const [MusicData, setMusicData] = useState<IMusicData>({ ...initialData });

  const UpdateStep = (step: number) => {
    setCurrentStep(step);
  };
  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    } else {
      setFinish(true);
    }
  };
  //step1
  const handleChangeStepOne = (key: MusicDataKeys, value: any) => {
    setMusicData((prev) => ({ ...prev, [key]: value }));
  };

  //step 2 functions
  const AddToTrackList = (audioFile: File) => {
    setMusicData({
      ...MusicData,
      trackList: [...MusicData.trackList, { file: audioFile }]
    });
    //focus on latest track index , function runs when track list length isn't updated , so it acts as an index
    setActiveTrack(MusicData.trackList.length);
    setCurrentStep(-1);
  };

  const RearrangeTrackList = (list: ITrack[]) => {
    setMusicData({ ...MusicData, trackList: list });
  };

  const EditTrack = (index: number) => {
    setActiveTrack(index);
    setCurrentStep(-1);
  };
  const DeleteTrack = (index: number) => {
    setMusicData((prev) => ({
      ...prev,
      trackList: prev.trackList.filter((_, i) => i !== index)
    }));
  };

  const updateTrack = (index: number, updatedTrack: ITrack) => {
    setMusicData((prevData) => ({
      ...prevData,
      trackList: prevData.trackList.map((track, i) =>
        i === index ? { ...track, ...updatedTrack } : track
      )
    }));
  };

  //step 3

  const updateSplit = (split: ITrackSplit[], index: number) => {
    setMusicData((prevData) => ({
      ...prevData,
      trackList: prevData.trackList.map((track, i) =>
        i === index ? { ...track, split: [...split] } : track
      )
    }));
  };

  const getNavigationState = () => {
    let canNext = false;
    let canPrevious = currentStep > 1;

    if (currentStep === 1) {
      canNext =
        !!MusicData.coverImage &&
        !!MusicData.primaryArtist &&
        !!MusicData.primaryArtist.id &&
        !!MusicData.primaryGenre &&
        !!MusicData.recordLabel &&
        !!MusicData.releaseLanguage &&
        !!MusicData.secondaryGenre &&
        !!MusicData.releaseType &&
        !!MusicData.releaseTitle;
    }

    if (currentStep === 2) {
      canNext = step2Checklist && MusicData.trackList.length > 0;
    }

    if (currentStep === 3) {
      canNext = true;
    }

    if (currentStep === 4) {
      canNext =
        !!MusicData.schedule.trackPrice &&
        !!MusicData.schedule.proposedReleaseDate;
    }

    if (currentStep === 5) {
      canNext = MusicData.stores.length > 0;
    }

    return { canNext, canPrevious };
  };

  const { canNext, canPrevious } = useMemo(getNavigationState, [
    currentStep,
    MusicData,
    step2Checklist
  ]);

  const Reset = () => {
    setMusicData({ ...initialData });
    setCurrentStep(1);
    setStep2Checklist(false);
    setActiveTrack(undefined);
    setFinish(false);
  };

  const BodyRef = useRef(null);

  useEffect(() => {
    scrollToTop(BodyRef);
  }, [currentStep]);

  return (
    <DashboardLayout>
      <>
        <PageLinks
          from={{ name: "Overview", link: "/overview" }}
          current="Upload Music"
        />
        <ProgressSteps
          currentStep={currentStep}
          steps={[
            "Release Details",
            "Track List",
            "Splits",
            "Schedule",
            "Distribution"
          ]}
        />
        <Body ref={BodyRef}>
          {currentStep === 1 ? (
            <StepOne
              musicData={MusicData}
              handleChange={(key, value) => handleChangeStepOne(key, value)}
            />
          ) : currentStep === 2 ? (
            <StepTwo
              deleteTrack={(index) => {
                DeleteTrack(index);
              }}
              trackList={MusicData.trackList}
              setCheckList={(value) => {
                setStep2Checklist(value);
              }}
              checkList={step2Checklist}
              addToTrackList={(file) => AddToTrackList(file)}
              editTrack={(track) => EditTrack(track)}
              rearrangeTrackList={(list) => RearrangeTrackList(list)}
              single={MusicData.releaseType === "Single"}
            />
          ) : currentStep === 3 ? (
            <StepThree
              trackList={MusicData.trackList}
              setTrackSplit={(split, index) => {
                updateSplit(split, index);
              }}
            />
          ) : currentStep === 4 ? (
            <StepFour
              setData={(data) => setMusicData({ ...MusicData, schedule: data })}
              data={MusicData.schedule}
            />
          ) : currentStep === 5 ? (
            <StepFive
              Stores={MusicData.stores}
              setStores={(stores) => {
                setMusicData({ ...MusicData, stores });
              }}
            />
          ) : currentStep === -1 ? (
            <Track
              data={MusicData.trackList[activeTrack || 0]}
              setTrack={(track) => {
                updateTrack(activeTrack || 0, track);
              }}
              updateStep={() => UpdateStep(2)}
            />
          ) : null}

          {finish && (
            <FinishModal
              reset={Reset}
              finish={finish}
              setFinish={(bol) => setFinish(bol)}
              musicData={MusicData}
            />
          )}
        </Body>

        {currentStep > 0 && (
          <StepsNav
            steps={5}
            currentStep={currentStep}
            canNext={canNext}
            canPrevious={canPrevious}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        )}
      </>
    </DashboardLayout>
  );
};

export default UploadMusic;

const Body = styled.div`
  width: 100%;
  padding-top: 2.4rem;
  padding-bottom: 6.4rem;
  min-height: 67vh;
`;
